import React from 'react';

import useGlobalState from '../../GlobalStateContext';
import { ROLE, RoleType } from '../../helpers/Permissions';
import ShowMetrics, { MetricsTimeRange, TYPE_METRICS } from '../../helpers/ShowMetrics';

const RenderIf = ({ hasRole, children }: { hasRole: RoleType; children: React.ReactNode }) =>
    useGlobalState().loggedAdmin.hasRole(hasRole) ? <>{children}</> : null;

export default function MetricsDashboard({ url, timeRange }: { url: string; timeRange: MetricsTimeRange }) {
    return (
        <div className="row metrics">
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/new"
                label="New Lottery Tickets"
                typeMetrics={TYPE_METRICS.VALUE_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/per_day"
                label="Lottery Tickets per Day"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/per_lottery"
                label="Lottery Tickets per Lottery"
                typeMetrics={TYPE_METRICS.PARTITION_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/per_status"
                label="Lottery Tickets per Status"
                typeMetrics={TYPE_METRICS.PARTITION_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/tickets_value"
                label="Lottery Tickets Value"
                typeMetrics={TYPE_METRICS.VALUE_METRIC}
                tooltip="Total sold tickets excluding discounts (Sum of <em>Ticket Value</em> by <em>Created At</em>)."
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/winnings"
                label="Winnings"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                tooltip="Total net prizes and winning percentage of tickets (Sum of <em>Prize Net</em> by <em>Draw Date</em>)."
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/sale_sum"
                label="Sale sum"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                tooltip="Total ticket and multi-ticket sales (Sum of ticket <em>Sell Amount Gross</em> or multi-ticket <em>Price Gross</em> by <em>Created At</em>)."
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/new_lines"
                label="New Lottery Ticket Lines"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/lines/per_day"
                label="Lottery Tickets Lines per Day"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="lottery_ticket/lines/per_lottery"
                label="Lottery Tickets Lines per Lottery"
                typeMetrics={TYPE_METRICS.PARTITION_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="user/new"
                label="New users"
                typeMetrics={TYPE_METRICS.PARTITION_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="user/per_day"
                label="Users per Day"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="user/ftp"
                label="FTP"
                typeMetrics={TYPE_METRICS.VALUE_METRIC}
                tooltip="First time purchase"
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="user/ftd"
                label="FTD"
                typeMetrics={TYPE_METRICS.VALUE_METRIC}
                tooltip="First time deposit"
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="user/stp"
                label="STP"
                typeMetrics={TYPE_METRICS.VALUE_METRIC}
                tooltip="Second time purchase"
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="user/std"
                label="STD"
                typeMetrics={TYPE_METRICS.VALUE_METRIC}
                tooltip="Second time deposit"
                timeRange={timeRange}
            />

            <ShowMetrics
                url="/crea/api/metrics"
                path="deposit/per_day"
                label="Deposits per Day"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="deposit/new"
                label="New Deposits"
                typeMetrics={TYPE_METRICS.VALUE_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="deposit/payment_cost"
                label="Sum of Deposits (User fee)"
                typeMetrics={TYPE_METRICS.VALUE_METRIC}
                tooltip="Total user fees of deposits (Sum of <em>User fee</em> by <em>Created At</em>)."
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="deposit/by_payment_method"
                label="Deposits by Payment Method"
                typeMetrics={TYPE_METRICS.PARTITION_METRIC}
                tooltip="Total count of confirmed deposits (Count of <em>Deposit</em> group by <em>Payment Method</em> by <em>Created At</em>)."
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="deposit/sum"
                label="Sum of Deposits"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                tooltip="Total transaction costs of deposits (Sum of <em>Transaction Cost</em> by <em>Date Confirmed</em>)."
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="withdrawal/per_day"
                label="Withdrawals per Day"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="withdrawal/sum"
                label="Sum of Withdrawals"
                typeMetrics={TYPE_METRICS.TREND_METRIC}
                tooltip="Total transaction costs of withdrawals (Sum of <em>Transaction Cost</em> by <em>Date Confirmed</em>)."
                timeRange={timeRange}
            />
            <ShowMetrics
                url="/crea/api/metrics"
                path="withdrawal/new"
                label="New Withdrawals"
                typeMetrics={TYPE_METRICS.VALUE_METRIC}
                timeRange={timeRange}
            />
            <RenderIf hasRole={ROLE.MASTER_AGENT}>
                <ShowMetrics
                    url={url}
                    path="master_agent_resellers_sale_sum"
                    label="Resellers Sale Sum"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_agent_resellers_revenue"
                    label="Resellers Revenue"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_agent_super_agents_revenue"
                    label="Super Agents Revenue"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_agent_revenue"
                    label="Master Agent Revenue"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_agent_tickets_winnings"
                    label="Master Agent Winnings"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
            </RenderIf>
            <RenderIf hasRole={ROLE.MASTER_AFFILIATE_AGENT}>
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_users"
                    label="Master Affiliate Agent Users"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_ftd"
                    label="Master Affiliate Agent FTD"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_ftp"
                    label="Master Affiliate Agent FTP"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_stp"
                    label="Master Affiliate Agent STP"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_std"
                    label="Master Affiliate Agent STD"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_deposits"
                    label="Master Affiliate Agent Deposits"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_deposits_sum"
                    label="Master Affiliate Agent Deposits sum"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_avg_user_value"
                    label="Master Affiliate Agent Average user value"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_avg_returning_user_value"
                    label="Master Affiliate Agent Average returning user value"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="master_affiliate_agent_earning"
                    label="Master Affiliate Agent Earning"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
            </RenderIf>
            <RenderIf hasRole={ROLE.AFFILIATE_OWNER}>
                <ShowMetrics
                    url={url}
                    path="affiliate_owner_users"
                    label="Affiliate Owner Users"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="affiliate_owner_ftd"
                    label="Affiliate Owner FTD"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="affiliate_owner_stp"
                    label="Affiliate Owner STP"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="affiliate_owner_std"
                    label="Affiliate Owner STD"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="affiliate_owner_deposits"
                    label="Affiliate Owner Deposits"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="affiliate_owner_deposits_sum"
                    label="Affiliate Owner Deposits sum"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="affiliate_owner_avg_user_value"
                    label="Affiliate Owner Average user value"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="affiliate_owner_avg_returning_user_value"
                    label="Affiliate Owner Average returning user value"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="affiliate_owner_earning"
                    label="Affiliate Owner Earning"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
            </RenderIf>
            <RenderIf hasRole={ROLE.SUPER_AGENT}>
                <ShowMetrics
                    url={url}
                    path="super_agent_resellers_sale_sum"
                    label="Resellers Sale Sum"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="super_agent_resellers_revenue"
                    label="Resellers Revenue"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="super_agent_revenue"
                    label="Super Agent Revenue"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
                <ShowMetrics
                    url={url}
                    path="super_agent_tickets_winnings"
                    label="Super Agent Ticket Winnings"
                    typeMetrics={TYPE_METRICS.VALUE_METRIC}
                    timeRange={timeRange}
                />
            </RenderIf>
        </div>
    );
}
